/**
 * Shared service worker registration/unregistration logic
 * Used by: crash monitoring, Taboola push, and potentially other features
 */

function hasServiceWorkerSupport() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    navigator.serviceWorker &&
    typeof navigator.serviceWorker.register === 'function'
  );
}

function isBuzzFeedServiceWorkerRegistration(registration) {
  const scriptURL =
    registration.active?.scriptURL ||
    registration.waiting?.scriptURL ||
    registration.installing?.scriptURL ||
    '';

  return scriptURL.endsWith('/service-worker.js');
}

export async function getBuzzFeedServiceWorkerRegistration() {
  if (!hasServiceWorkerSupport()) {
    return null;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();

  return registrations.find(isBuzzFeedServiceWorkerRegistration) || null;
}

/**
 * Register the BuzzFeed service worker
 * Multiple calls are safe - browser handles idempotency
 */
export async function registerServiceWorker() {
  if (!hasServiceWorkerSupport()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    await navigator.serviceWorker.ready;

    // On first page load, wait for SW to claim control
    if (!navigator.serviceWorker.controller) {
      const claimed = await new Promise((resolve) => {
        let timeoutId;
        const onControllerChange = () => {
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
          resolve(true);
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });
        timeoutId = setTimeout(() => {
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
          resolve(false);
        }, 3000); // 3 second timeout
      });

      if (!claimed) {
        return null;
      }
    }

    return registration;
  } catch (error) {
    console.error('[SW Manager] Registration failed:', error);
    return null;
  }
}

/**
 * Unregister the service worker
 * Note: Only unregister if NO features are using it
 */
export async function unregisterServiceWorker() {
  if (!hasServiceWorkerSupport()) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      if (isBuzzFeedServiceWorkerRegistration(registration)) {
        await registration.unregister();
      }
    }
  } catch (err) {
    console.error('[SW Manager] Error unregistering service worker:', err);
  }
}
